
import React, { useState, useEffect } from 'react';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { updateUserProfile } from '../../services/firestore/userService';
import { getTenant, updateTenant } from '../../services/firestore/tenantService';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';

const UserProfile = ({ userData }) => {
    const { success, error: toastError } = useToast();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const isCoordinator = userData?.role === 'coordinator' || userData?.role === 'consultant';

    // Profile State
    const [profileForm, setProfileForm] = useState({
        fullName: userData?.fullName || '',
        phone: userData?.phone || '',
        email: userData?.email || ''
    });

    // Security State
    const [securityForm, setSecurityForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Tenant / Company State
    const [tenantForm, setTenantForm] = useState({
        name: '',
        description: '',
        taxNumber: '',
        taxOffice: '',
        billingEmail: '',
        address: '',
        country: '',
        city: '',
        district: '',
        companyPhone: ''
    });

    const [paymentInfo, setPaymentInfo] = useState({
        cardNumber: '**** **** **** 4242',
        expiry: '12/28',
        cardHolder: ''
    });

    const [isSecureVisible, setIsSecureVisible] = useState(false);
    const [authModal, setAuthModal] = useState(false);
    const [authPassword, setAuthPassword] = useState('');

    useEffect(() => {
        if (isCoordinator && userData?.tenantId) {
            loadTenantData();
        }
    }, [userData?.tenantId, isCoordinator]);

    const loadTenantData = async () => {
        try {
            const data = await getTenant(userData.tenantId);
            if (data) {
                setTenantForm({
                    name: data.name || '',
                    description: data.description || '',
                    taxNumber: data.taxNumber || '',
                    taxOffice: data.taxOffice || '',
                    billingEmail: data.billingEmail || '',
                    address: data.address || '',
                    country: data.country || '',
                    city: data.city || '',
                    district: data.district || '',
                    companyPhone: data.companyPhone || ''
                });
                setPaymentInfo(prev => ({
                    ...prev,
                    cardHolder: data.cardHolder || userData.fullName
                }));
            }
        } catch (error) {
            console.error('Tenant load error:', error);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await updateUserProfile(userData.tenantId, userData.id, {
                fullName: profileForm.fullName,
                phone: profileForm.phone
            });
            success('Profil bilgileriniz başarıyla güncellendi.');
        } catch (error) {
            toastError('Güncelleme başarısız: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (securityForm.newPassword !== securityForm.confirmPassword) {
            toastError('Yeni şifreler eşleşmiyor.');
            return;
        }

        setLoading(true);
        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, securityForm.currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, securityForm.newPassword);
            success('Şifreniz başarıyla değiştirildi.');
            setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toastError('Şifre değiştirilemedi: ' + (error.code === 'auth/wrong-password' ? 'Mevcut şifre hatalı.' : error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleTenantUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await updateTenant(userData.tenantId, tenantForm);
            success('Şirket bilgileri güncellendi.');
        } catch (error) {
            toastError('Güncelleme başarısız: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const verifyForSecureAction = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const credential = EmailAuthProvider.credential(userData.email, authPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);
            setIsSecureVisible(true);
            setAuthModal(false);
            setAuthPassword('');
            success('Kimlik doğrulandı, güvenli erişim açıldı.');
        } catch (error) {
            toastError('Şifre hatalı. Lütfen tekrar deneyin.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container p-xl">
            <div className="user-profile-holistic">

                {/* Left: User Summary Section */}
                <div className="profile-summary-section">
                    <div className="avatar-large">
                        {userData?.fullName?.charAt(0)}
                    </div>
                    <div className="text-center mb-xl">
                        <h2 className="mb-xs" style={{
                            textShadow: '0 0 10px rgba(var(--primary-rgb), 0.5)',
                            fontSize: '1.8rem'
                        }}>
                            {userData?.fullName}
                        </h2>
                        {isCoordinator && tenantForm.description && (
                            <div className="badge badge-primary-glass mt-xs" style={{
                                fontSize: '0.75rem',
                                letterSpacing: '1px',
                                background: 'rgba(var(--primary-rgb), 0.1)',
                                border: '1px solid rgba(var(--primary-rgb), 0.2)',
                                boxShadow: '0 0 15px rgba(var(--primary-rgb), 0.1)'
                            }}>
                                {tenantForm.description.toUpperCase()}
                            </div>
                        )}
                    </div>

                    <p className="text-secondary text-sm mb-xl" style={{ textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 700, opacity: 0.7 }}>
                        {userData?.transporterName || userData?.role}
                    </p>

                    <div style={{ width: '100%', padding: '20px 0', borderTop: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-light">Sistem Durumu:</span>
                            <span className="badge" style={{ background: 'var(--status-entered)', color: 'white', fontSize: '10px' }}>AKTİF PERSONEL</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-light">Bağlı Kurum:</span>
                            <span className="text-secondary" style={{ fontWeight: 600 }}>
                                {userData?.transporterName ? `${userData.transporterName} (${userData.tenantName || 'RTC'})` : (tenantForm.name || userData?.tenantName || 'RTC')}
                            </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-light">Bilgi Doğrulama:</span>
                            <span className="text-secondary" style={{ fontWeight: 600 }}>Onaylı Üye</span>
                        </div>
                    </div>
                </div>

                {/* Right: Settings Section */}
                <div className="profile-settings-section">
                    {/* Tabs */}
                    <div className="flex gap-lg border-b border-border mb-xl">
                        <button
                            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                            onClick={() => setActiveTab('profile')}
                            style={{
                                padding: '12px 24px',
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === 'profile' ? '2px solid var(--primary-color)' : '2px solid transparent',
                                color: activeTab === 'profile' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                cursor: 'pointer',
                                fontWeight: 700,
                                fontSize: '1rem',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Profilim
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
                            onClick={() => setActiveTab('security')}
                            style={{
                                padding: '12px 24px',
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === 'security' ? '2px solid var(--primary-color)' : '2px solid transparent',
                                color: activeTab === 'security' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                cursor: 'pointer',
                                fontWeight: 700,
                                fontSize: '1rem',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Güvenlik
                        </button>
                        {isCoordinator && (
                            <button
                                className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
                                onClick={() => setActiveTab('company')}
                                style={{
                                    padding: '12px 24px',
                                    background: 'none',
                                    border: 'none',
                                    borderBottom: activeTab === 'company' ? '2px solid var(--primary-color)' : '2px solid transparent',
                                    color: activeTab === 'company' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Şirket & Ödeme
                            </button>
                        )}
                    </div>

                    {/* Content Area (No Fade In Anim) */}
                    <div className="profile-tab-content">
                        {activeTab === 'profile' && (
                            <form onSubmit={handleProfileUpdate} className="grid grid-cols-2 gap-xl">
                                <div className="form-group glass-input-container">
                                    <label className="form-label">Tam Ad Soyad</label>
                                    <input
                                        className="input"
                                        value={profileForm.fullName}
                                        onChange={e => setProfileForm({ ...profileForm, fullName: e.target.value })}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    />
                                </div>
                                <div className="form-group glass-input-container">
                                    <label className="form-label">E-posta Adresi</label>
                                    <input
                                        className="input"
                                        value={profileForm.email}
                                        readOnly
                                        style={{ opacity: 0.5, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', cursor: 'not-allowed' }}
                                    />
                                </div>
                                <div className="form-group glass-input-container">
                                    <label className="form-label">İletişim Telefonu</label>
                                    <input
                                        className="input"
                                        placeholder="05xx ..."
                                        value={profileForm.phone}
                                        onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    />
                                </div>
                                <div className="col-span-2 flex justify-center mt-lg">
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        loading={loading}
                                        className="btn-led"
                                        style={{ padding: '12px 60px' }}
                                    >
                                        Profilimi Güncelle
                                    </Button>
                                </div>
                            </form>
                        )}

                        {activeTab === 'security' && (
                            <form onSubmit={handlePasswordChange} className="flex flex-col gap-xl max-w-md">
                                <div className="form-group glass-input-container">
                                    <label className="form-label">Mevcut Şifreniz</label>
                                    <input
                                        type="password"
                                        className="input"
                                        required
                                        value={securityForm.currentPassword}
                                        onChange={e => setSecurityForm({ ...securityForm, currentPassword: e.target.value })}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    />
                                </div>
                                <div style={{ height: '1px', background: 'var(--border-color)', margin: '10px 0' }} />
                                <div className="form-group glass-input-container">
                                    <label className="form-label">Yeni Şifre</label>
                                    <input
                                        type="password"
                                        className="input"
                                        required
                                        minLength={6}
                                        value={securityForm.newPassword}
                                        onChange={e => setSecurityForm({ ...securityForm, newPassword: e.target.value })}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    />
                                </div>
                                <div className="form-group glass-input-container">
                                    <label className="form-label">Yeni Şifre (Yeniden Yazın)</label>
                                    <input
                                        type="password"
                                        className="input"
                                        required
                                        value={securityForm.confirmPassword}
                                        onChange={e => setSecurityForm({ ...securityForm, confirmPassword: e.target.value })}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    />
                                </div>
                                <div className="flex justify-end mt-lg">
                                    <Button variant="primary" type="submit" loading={loading} style={{ padding: '12px 40px' }}>
                                        Şifremi Değiştir
                                    </Button>
                                </div>
                            </form>
                        )}

                        {activeTab === 'company' && (
                            <div className="flex flex-col gap-2xl">
                                <form onSubmit={handleTenantUpdate}>
                                    <h4 className="mb-lg" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        🏢 Kurumsal Bilgiler
                                    </h4>
                                    <div className="grid grid-cols-2 gap-xl">
                                        <div className="form-group glass-input-container border-glow">
                                            <label className="form-label">Resmi Şirket Adı</label>
                                            <input
                                                className="input"
                                                value={tenantForm.name}
                                                onChange={e => setTenantForm({ ...tenantForm, name: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>
                                        <div className="form-group glass-input-container">
                                            <label className="form-label">Şirket Telefonu</label>
                                            <input
                                                className="input"
                                                value={tenantForm.companyPhone}
                                                onChange={e => setTenantForm({ ...tenantForm, companyPhone: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>
                                        <div className="form-group glass-input-container">
                                            <label className="form-label">Vergi Numarası</label>
                                            <input
                                                className="input"
                                                value={tenantForm.taxNumber}
                                                onChange={e => setTenantForm({ ...tenantForm, taxNumber: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>
                                        <div className="form-group glass-input-container">
                                            <label className="form-label">Vergi Dairesi</label>
                                            <input
                                                className="input"
                                                value={tenantForm.taxOffice}
                                                onChange={e => setTenantForm({ ...tenantForm, taxOffice: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>

                                        {/* Address Row */}
                                        <div className="col-span-2 form-group glass-input-container">
                                            <label className="form-label">Açık Adres</label>
                                            <textarea
                                                className="input"
                                                rows={2}
                                                value={tenantForm.address}
                                                onChange={e => setTenantForm({ ...tenantForm, address: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', minHeight: '80px', paddingTop: '10px' }}
                                            />
                                        </div>

                                        <div className="form-group glass-input-container">
                                            <label className="form-label">Ülke</label>
                                            <input
                                                className="input"
                                                value={tenantForm.country}
                                                onChange={e => setTenantForm({ ...tenantForm, country: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>
                                        <div className="form-group glass-input-container">
                                            <label className="form-label">Şehir</label>
                                            <input
                                                className="input"
                                                value={tenantForm.city}
                                                onChange={e => setTenantForm({ ...tenantForm, city: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>
                                        <div className="form-group glass-input-container">
                                            <label className="form-label">İlçe</label>
                                            <input
                                                className="input"
                                                value={tenantForm.district}
                                                onChange={e => setTenantForm({ ...tenantForm, district: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>
                                        <div className="form-group glass-input-container">
                                            <label className="form-label">Kısa Tanım (Etiket)</label>
                                            <input
                                                className="input"
                                                value={tenantForm.description}
                                                onChange={e => setTenantForm({ ...tenantForm, description: e.target.value })}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                            />
                                        </div>

                                        <div className="col-span-2 flex justify-center">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                loading={loading}
                                                className="btn-led"
                                                style={{ padding: '12px 60px' }}
                                            >
                                                Şirket Bilgilerini Kaydet
                                            </Button>
                                        </div>
                                    </div>
                                </form>

                                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '30px' }}>
                                    <div className="flex justify-between items-center mb-xl">
                                        <h4 style={{ color: 'var(--text-primary)' }}>💳 Ödeme & Faturalandırma</h4>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setAuthModal(true)}
                                            style={{ color: 'var(--primary-color)', fontWeight: 700, background: 'rgba(var(--primary-rgb), 0.05)' }}
                                        >
                                            {isSecureVisible ? '✓ Bilgiler Açık' : '🔐 Güvenli Görüntüle'}
                                        </Button>
                                    </div>

                                    <div className="card-premium" style={{
                                        background: 'rgba(255,255,255,0.02)',
                                        border: '1px dashed var(--border-color)',
                                        borderRadius: '16px',
                                        boxShadow: isSecureVisible ? '0 0 30px rgba(0, 255, 127, 0.05)' : 'none'
                                    }}>
                                        {isSecureVisible ? (
                                            <div className="flex flex-col gap-lg p-md">
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div className="text-secondary text-xs uppercase mb-xs">Kayıtlı Kart</div>
                                                        <div style={{ fontSize: '1.2rem', fontFamily: 'var(--font-mono)', letterSpacing: '2px', color: 'var(--text-primary)' }}>
                                                            {paymentInfo.cardNumber}
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div className="text-secondary text-xs uppercase mb-xs">Son Kullanma</div>
                                                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{paymentInfo.expiry}</div>
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div className="text-secondary text-xs uppercase mb-xs">Kart Sahibi</div>
                                                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{paymentInfo.cardHolder}</div>
                                                    </div>
                                                    <Button variant="secondary" size="sm" onClick={() => toastError('Ödeme sistemi şu an sadece görüntüleme modundadır.')} style={{ opacity: 0.7 }}>
                                                        Kartı Değiştir
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center p-xl">
                                                <div style={{ fontSize: '2rem', marginBottom: '10px', filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.5))' }}>🔐</div>
                                                <p className="text-secondary text-sm">Finansal veriler güvenlik sebebiyle gizlenmiştir.</p>
                                                <p className="text-light text-xs mt-xs">Görüntülemek için profil şifrenizi doğrulayın.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={authModal}
                onClose={() => setAuthModal(false)}
                title="Yüksek Güvenlikli Alan Doğrulaması"
                footer={
                    <div className="flex justify-end gap-md">
                        <Button variant="secondary" onClick={() => setAuthModal(false)}>Vazgeç</Button>
                        <Button variant="primary" onClick={verifyForSecureAction} loading={loading}>Doğrula ve Göster</Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-md">
                    <p className="text-secondary text-sm">
                        Finansal ve şirket verilerine erişmek için mevcut şifrenizi girmeniz gerekmektedir.
                    </p>
                    <div className="form-group glass-input-container">
                        <label className="form-label">Şifreniz</label>
                        <input
                            type="password"
                            className="input"
                            autoFocus
                            placeholder="******"
                            value={authPassword}
                            onChange={e => setAuthPassword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && verifyForSecureAction(e)}
                            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default UserProfile;
